import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Download, FileText, X } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useI18n, bi } from "@/i18n";
import Reveal, { Cross } from "@/components/Reveal";

export default function Projects() {
  const { lang, t } = useI18n();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/projects")
      .then((r) => setProjects(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (selected) {
      window.__lenis?.stop();
      const onKey = (e) => e.key === "Escape" && setSelected(null);
      window.addEventListener("keydown", onKey);
      return () => {
        window.__lenis?.start();
        window.removeEventListener("keydown", onKey);
      };
    }
  }, [selected]);

  return (
    <section id="projects" className="relative scroll-mt-20 border-t border-line px-6 py-24 md:px-10 md:py-32 xl:px-16" data-testid="projects-section">
      <Cross className="left-4 bottom-16 hidden md:block" />
      <div className="mx-auto max-w-[1440px]">
        <Reveal>
          <div className="text-[11px] tracking-[0.3em] text-gold">{t.projects.label}</div>
          <h2 className="mt-5 font-display text-3xl font-bold text-white md:text-4xl" data-testid="projects-title">{t.projects.title}</h2>
          <p className="mt-4 max-w-xl text-xs leading-relaxed text-zinc-500 md:text-sm">{t.projects.sub}</p>
        </Reveal>

        {loading ? (
          <div className="mt-16 border border-line bg-panel p-10 text-center text-xs tracking-[0.3em] text-zinc-500" data-testid="projects-loading">
            {t.projects.loading}
            <span className="ml-2 inline-block h-3 w-2 bg-gold blink" />
          </div>
        ) : projects.length === 0 ? (
          <div className="mt-16 border border-line bg-panel p-10 text-center text-xs tracking-[0.3em] text-zinc-500" data-testid="projects-empty">{t.projects.empty}</div>
        ) : (
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="projects-grid">
            {projects.map((p, i) => (
              <Reveal
                key={p.id}
                delay={(i % 3) * 0.1}
                className={`group cursor-pointer border border-line bg-panel transition-all duration-300 hover:border-gold/50 ${i === 0 ? "md:col-span-2 lg:col-span-2" : ""}`}
                onClick={() => setSelected(p)}
                data-testid={`project-card-${i}`}
              >
                <ProjectCard p={p} i={i} lang={lang} t={t} />
              </Reveal>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {selected && <ProjectModal p={selected} lang={lang} t={t} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </section>
  );
}

function ProjectCard({ p, i, lang, t }) {
  return (
    <div className="flex h-full flex-col" data-testid="project-card">
      <div className="relative h-48 overflow-hidden md:h-56" data-testid="project-card-image">
        {p.image ? (
          <img src={p.image} alt={bi(lang, p, "title")} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="flex h-full items-center justify-center bg-ink font-display text-3xl text-zinc-800">TIA</div>
        )}
        <span className="absolute left-3 top-3 border border-gold/60 bg-black/70 px-2 py-1 text-[10px] tracking-[0.25em] text-gold backdrop-blur">
          {t.projects.prj}-{String(i + 1).padStart(3, "0")}
        </span>
        {p.year && <span className="absolute right-3 top-3 bg-black/70 px-2 py-1 text-[10px] tracking-[0.25em] text-zinc-300 backdrop-blur">{p.year}</span>}
      </div>
      <div className="flex flex-1 flex-col p-6">
        <h3 className="font-display text-base font-bold leading-snug text-white transition-colors duration-200 group-hover:text-gold md:text-lg" data-testid="project-card-title">
          {bi(lang, p, "title")}
        </h3>
        <div className="mt-4 flex flex-wrap gap-2" data-testid="project-card-tech">
          {(p.tech || []).slice(0, 4).map((tech) => (
            <span key={tech} className="border border-line px-2 py-1 text-[10px] tracking-[0.15em] text-zinc-400">{tech}</span>
          ))}
        </div>
        <div className="mt-auto flex items-center justify-between pt-6 text-[11px] tracking-[0.2em]">
          <span className="flex items-center gap-2 text-zinc-500">
            <FileText size={12} />
            {p.attachments?.length ? `${t.projects.attachments}: ${p.attachments.length}` : t.projects.noDocs}
          </span>
          <span className="flex items-center gap-1.5 text-gold opacity-0 transition-opacity duration-300 group-hover:opacity-100">
            {t.projects.open} <ArrowUpRight size={14} />
          </span>
        </div>
      </div>
    </div>
  );
}

function ProjectModal({ p, lang, t, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[150] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm md:p-8"
      onClick={onClose}
      data-testid="project-modal"
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 20, opacity: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
        data-lenis-prevent
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto overscroll-contain border border-line bg-ink"
        data-testid="project-modal-panel"
      >
        <div className="relative h-56 md:h-72">
          {p.image && <img src={p.image} alt={bi(lang, p, "title")} className="h-full w-full object-cover" />}
          <button
            onClick={onClose}
            data-testid="project-modal-close"
            className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center border border-line bg-black/70 text-white backdrop-blur transition-colors duration-200 hover:border-gold hover:text-gold"
            aria-label={t.projects.close}
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-6 md:p-10">
          <div className="flex flex-wrap items-center gap-4 text-[11px] tracking-[0.25em] text-zinc-500">
            {p.year && <span>{p.year}</span>}
            <span className="text-gold">TIA PORTAL</span>
          </div>
          <h3 className="mt-4 font-display text-2xl font-bold text-white md:text-3xl" data-testid="project-modal-title">{bi(lang, p, "title")}</h3>
          <p className="mt-6 whitespace-pre-line text-sm leading-relaxed text-zinc-300" data-testid="project-modal-desc">{bi(lang, p, "desc")}</p>

          <div className="mt-8">
            <div className="text-[10px] tracking-[0.3em] text-zinc-500">{t.projects.techLabel}</div>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="project-modal-tech">
              {(p.tech || []).map((tech) => (
                <span key={tech} className="border border-line bg-panel px-3 py-1.5 text-[11px] tracking-[0.12em] text-zinc-300">{tech}</span>
              ))}
            </div>
          </div>

          <div className="mt-8 border-t border-line pt-6">
            <div className="text-[10px] tracking-[0.3em] text-zinc-500">{t.projects.attachments}</div>
            {p.attachments?.length ? (
              <div className="mt-4 space-y-2" data-testid="project-modal-attachments">
                {p.attachments.map((a, i) => (
                  <a
                    key={i}
                    href={fileUrl(a.url)}
                    download={a.name}
                    data-testid="project-download-btn"
                    className="group flex items-center justify-between border border-line bg-panel px-4 py-3 text-xs text-zinc-300 transition-colors duration-200 hover:border-gold hover:text-gold"
                  >
                    <span className="flex items-center gap-3">
                      <FileText size={14} className="text-gold" />
                      {a.name}
                    </span>
                    <span className="flex items-center gap-2 tracking-[0.2em]">
                      {a.size ? `${Math.max(1, Math.round(a.size / 1024))} KB` : ""}
                      <Download size={14} className="transition-transform duration-200 group-hover:translate-y-0.5" />
                    </span>
                  </a>
                ))}
              </div>
            ) : (
              <div className="mt-4 border border-dashed border-line px-4 py-6 text-center text-[11px] tracking-[0.25em] text-zinc-600">{t.projects.noDocs}</div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
